import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import { Moment } from 'moment';

/**
 * Returns the current timestamp as an ISO 8601 string.
 * NOTE: For exact Firestore server time, use FieldValue.serverTimestamp() and refetch the document.
 * @returns The current date-time in ISO 8601 format.
 */
export const nowISOString = (): string => new Date().toISOString();

/**
 * Returns the given moment as an ISO 8601 string.
 * @param moment
 * @returns {string}
 */
export const momentISOString = (moment: Moment): string => moment.toISOString();

/**
 * Returns the given firestore timestamp as an ISO 8601 string.
 * @param timestamp
 * @returns {string}
 */
export const timestampISOString = (timestamp: Timestamp): string =>
  timestamp.toDate().toISOString();

/**
 * Returns a Moment object for the given ISO 8601 string.
 * @param isoString
 * @returns {Moment}
 */
export const isoStringToMoment = (iso_string: string): Moment => moment(iso_string);

/**
 * Returns an array of formatted month strings as 'YYYY-MM-DD' for each month between a start and an end Moment (inclusive).
 * @param {Moment | null} start Start date of range.
 * @param {Moment | null} end End date of range.
 * @returns {Array<string>}
 */
export const generateMonthRangeStrings = (
  start: Moment | null,
  end: Moment | null
): Array<string> => {
  // If start and end are null, return empty array.
  if (!start && !end) {
    return [];
  }

  // If only start is null, use end month only.
  if (!start && end) {
    return [end.clone().startOf('month').format('YYYY-MM-DD')];
  }

  // If only end is null, use start month only.
  if (start && !end) {
    return [start.clone().startOf('month').format('YYYY-MM-DD')];
  }

  // If both are valid moments.
  const start_month: Moment = start!.clone().startOf('month');
  const end_month: Moment = end!.clone().startOf('month');

  const formatted_months: Array<string> = [];
  const current_month: Moment = start_month.clone();

  // Generate array of formatted month strings between start and end (inclusive).
  while (current_month.isSameOrBefore(end_month, 'month')) {
    formatted_months.push(current_month.format('YYYY-MM-DD'));
    current_month.add(1, 'month');
  }

  return formatted_months;
};

/**
 * Snap a local or utc moment to midnight on the same given day, as utc, effectively removing the timezone and timestamp dimensions.
 * @param {Moment} p_moment Moment to snap
 * @returns {Moment}
 */
export const momentSnapDayUTC = (p_moment: Moment): Moment => {
  return moment.utc({
    year: p_moment.year(),
    month: p_moment.month(),
    date: p_moment.date(),
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  });
};
